/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import {
	type Nudge,
	useAINudges,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useRovoButtonEventsInstrumentation } from '../../../../../hooks/useRovoButtonEventsInstrumentation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isBrowser } from '../../../../common/utils/is-browser';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useFetchNudgeRecommendations } from '../../../hooks/useFetchNudgeRecommendations';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { NudgeContent } from '../../nudge-content';

const NUDGE_DISMISS_TIMEOUT = 30000;
const ANIMATION_DURATION = 200;

export const AIProactiveNudge = () => {
	const [dismissedIds, setDismissedIds] = useState<Record<string, boolean>>({});
	const [acceptedIds, setAcceptedIds] = useState<Record<string, boolean>>({});

	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const [triggerId] = useState(() => uuid());
	const [isNudgeVisible, setIsNudgeVisible] = useState(false);
	const [prevNudgeId, setPrevNudgeId] = useState<string | null>(null);
	const [isMouseHovering, setIsMouseHovering] = useState<boolean>(false);
	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

	const registeredNudges = useAINudges();
	const surfaceMetadata = useAISurfaceMetadata();
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const { changeView } = useViewAIActions();
	const { recommendations, loading, error } = useFetchNudgeRecommendations(
		triggerId,
		Boolean(registeredNudges.length),
		isBrowser() ? window.location.href : '',
		surfaceMetadata?.product,
	);
	const recommendation = useMemo(() => recommendations?.[0], [recommendations]);

	const activeNudge: Nudge | null = useMemo(() => {
		if (loading || error || !recommendation) {
			return null;
		}

		/**
		 * Nudge must not be previously dismissed or accepted
		 * to be considered active.
		 */
		if (dismissedIds[recommendation.nudge_id] || acceptedIds[recommendation.nudge_id]) {
			return null;
		}

		return (
			registeredNudges.find(
				(nudge: Nudge) => nudge.id === recommendation.feature_details.feature_id,
			) || null
		);
	}, [loading, error, recommendation, dismissedIds, acceptedIds, registeredNudges]);

	useEffect(() => {
		if (activeNudge && recommendation && recommendation.nudge_id !== prevNudgeId) {
			setPrevNudgeId(recommendation.nudge_id);

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'rendered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
			window.requestAnimationFrame(() => {
				/**
				 * Needs to be set to visible in next tick, otherwise
				 * it will not animate
				 */
				setIsNudgeVisible(true);
			});
		}
	}, [activeNudge, fireRovoButtonUIEvent, prevNudgeId, recommendation, surfaceMetadata]);

	const onDismissClick = useCallback(() => {
		if (activeNudge && recommendation) {
			setDismissedIds((prevIds) => ({ ...prevIds, [recommendation.nudge_id]: true }));

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'dismissed',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					dismissedMethod: 'manual',
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}
	}, [
		activeNudge,
		fireRovoButtonUIEvent,
		recommendation,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	const onNudgeClick = useCallback(() => {
		if (activeNudge && recommendation) {
			setAcceptedIds((prevIds) => ({ ...prevIds, [recommendation.nudge_id]: true }));

			if (activeNudge.viewConfig) {
				changeView(activeNudge);
			}
			activeNudge.onClick?.();

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: activeNudge.id,
					eventNudgeId: nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}
	}, [
		activeNudge,
		changeView,
		fireRovoButtonUIEvent,
		recommendation,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	const onWrapperMouseEnter = useCallback(() => {
		setIsMouseHovering(true);

		hoverTimerRef.current = setTimeout(() => {
			const nudgeId = activeNudge ? `${activeNudge.title} - ${activeNudge.id}` : '';
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'hovered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoProactiveNudge',
				attributes: {
					product: surfaceMetadata.product,
					productPage: surfaceMetadata.contentType,
					pageMode: surfaceMetadata.pageMode,
					nudgeId,
				},
				product: surfaceMetadata.product,
			});
		}, 1000);
	}, [
		activeNudge,
		fireRovoButtonUIEvent,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	const onWrapperMouseLeave = useCallback(() => {
		setIsMouseHovering(false);

		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, []);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout | undefined;
		let slideoutTimeoutId: NodeJS.Timeout | undefined;
		if (activeNudge && recommendation && !isMouseHovering) {
			timeoutId = setTimeout(() => {
				setDismissedIds((prevIds) => ({ ...prevIds, [recommendation.nudge_id]: true }));
				fireRovoButtonUIEvent({
					source: surfaceMetadata.contentType,
					action: 'dismissed',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoProactiveNudge',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
						id: activeNudge.id,
						timerDuration: NUDGE_DISMISS_TIMEOUT,
						dismissedMethod: 'timer_expired',
					},
					product: surfaceMetadata.product,
				});
			}, NUDGE_DISMISS_TIMEOUT);

			slideoutTimeoutId = setTimeout(() => {
				setIsNudgeVisible(false);
			}, NUDGE_DISMISS_TIMEOUT - ANIMATION_DURATION);
		}

		/**
		 * On mouse enter, isMouseHovering is set to true, will trigger
		 *  cleanup function to clear timeout.
		 */
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (slideoutTimeoutId) {
				clearTimeout(slideoutTimeoutId);
			}
		};
	}, [
		activeNudge,
		fireRovoButtonUIEvent,
		isMouseHovering,
		recommendation,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
		surfaceMetadata.product,
	]);

	if (!activeNudge) {
		return null;
	}

	return (
		<NudgeContent
			activeNudge={activeNudge}
			isNudgeVisible={isNudgeVisible}
			onWrapperMouseEnter={onWrapperMouseEnter}
			onWrapperMouseLeave={onWrapperMouseLeave}
			onNudgeClick={onNudgeClick}
			onDismissClick={onDismissClick}
		/>
	);
};
